import { Button, FormInstance, Table } from "antd";
import { renderDateFormated } from "../../utils";
import { Excel } from "antd-table-saveas-excel";
import { DownloadOutlined } from "@ant-design/icons";
import { useState } from "react";
import { toast } from "react-toastify";
import { columnsAssemble } from "./constants";
import { formatCurrency } from "../../utils/helpers/ticketHelpers";

interface ReportDataProps {
  key: string;
  title: string;
  contact: string;
  createdAt: string;
  endAt: string;
  status: string;
  owner: string;
  description: string;
  scheduleCount?: number;
  scheduleDates?: string[];
  scheduleAgendaNames?: string;
  grossValue?: number;
  couponValue?: number;
  negotiatedValue?: number;
  minhaVisitaClientName?: string;
  discountPercentage?: number;
}

interface TableDataProps {
  data: ReportDataProps[];
  searchForm: FormInstance;
  loading: boolean;
}

export const TableData = ({ data, searchForm, loading }: TableDataProps) => {
  const [exportLoading, setExportLoading] = useState(false);
  const formatScheduleDates = (dates?: string[]) =>
    dates?.map((date) => renderDateFormated(date, false, false)).join(", ") ??
    "";

  const columns: any = [
    {
      title: "Título",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Contato",
      dataIndex: "contact",
      key: "contact",
      sorter: (a: any, b: any) =>
        a.contact ? a.contact.localeCompare(b.contact) : "",
    },
    {
      title: "Inclusão",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: any) =>
        date ? renderDateFormated(date, false, true) : "",
    },
    {
      title: "Conclusão",
      dataIndex: "endAt",
      key: "endAt",
      sorter: (a: any, b: any) =>
        new Date(a.endAt).getTime() - new Date(b.endAt).getTime(),
      render: (date: any) =>
        date ? renderDateFormated(date, false, true) : "",
    },
    {
      title: "Situação",
      dataIndex: "status",
      key: "status",
      sorter: (a: any, b: any) =>
        a.status ? a.status.localeCompare(b.status) : "",
    },
    {
      title: "Data Envio Medição",
      dataIndex: "sentToApp",
      key: "sentToApp",
      sorter: (a: any, b: any) => a.sentToApp > b.sentToApp,
      render: (date: string): string =>
        date ? renderDateFormated(date, false, false) : "",
    },
    {
      title: "Data Preenchimento",
      dataIndex: "filledAt",
      key: "filledAt",
      sorter: (a: any, b: any) => {
        return new Date(a.filledAt).getTime() - new Date(b.filledAt).getTime();
      },
      render: (date: string): string =>
        date ? renderDateFormated(date, false, false) : "",
    },
    {
      title: "Medidor",
      dataIndex: "owner",
      key: "owner",
      sorter: (a: any, b: any) =>
        a.owner ? a.owner.localeCompare(b.owner) : "",
    },

    {
      title: "Vendedor",
      dataIndex: "seller",
      key: "seller",
      sorter: (a: any, b: any) =>
        a.seller ? a.seller.localeCompare(b.seller) : "",
    },
  ];

  const checklistReportColumns: any = [
    {
      title: "Quantidade de Agendamentos",
      dataIndex: "scheduleCount",
      key: "scheduleCount",
      sorter: (a: any, b: any) => a.scheduleCount - b.scheduleCount,
    },
    {
      title: "Data do Agendamento",
      dataIndex: "scheduleDates",
      key: "scheduleDates",
      sorter: (a: any, b: any) =>
        new Date(a.scheduleDates?.[0] ?? 0).getTime() -
        new Date(b.scheduleDates?.[0] ?? 0).getTime(),
      render: (dates: string[]) => formatScheduleDates(dates),
    },
    {
      title: "Agenda",
      dataIndex: "scheduleAgendaNames",
      key: "scheduleAgendaNames",
      sorter: (a: any, b: any) =>
        (a.scheduleAgendaNames ?? "").localeCompare(
          b.scheduleAgendaNames ?? ""
        ),
    },
    {
      title: "Valor Bruto",
      dataIndex: "grossValue",
      key: "grossValue",
      sorter: (a: any, b: any) => a.grossValue - b.grossValue,
      render: (value: number) => formatCurrency(value ?? 0),
    },
    {
      title: "Valor dos Cupons",
      dataIndex: "couponValue",
      key: "couponValue",
      sorter: (a: any, b: any) => a.couponValue - b.couponValue,
      render: (value: number) => formatCurrency(value ?? 0),
    },
    {
      title: "Valor Negociado",
      dataIndex: "negotiatedValue",
      key: "negotiatedValue",
      sorter: (a: any, b: any) => a.negotiatedValue - b.negotiatedValue,
      render: (value: number) => formatCurrency(value ?? 0),
    },
    {
      title: "Cliente Minha Visita",
      dataIndex: "minhaVisitaClientName",
      key: "minhaVisitaClientName",
      sorter: (a: any, b: any) =>
        a.minhaVisitaClientName
          ? a.minhaVisitaClientName.localeCompare(b.minhaVisitaClientName)
          : "",
    },
    {
      title: "Percentual de Desconto",
      dataIndex: "discountPercentage",
      key: "discountPercentage",
      sorter: (a: any, b: any) =>
        a.discountPercentage - b.discountPercentage,
      render: (value: number) => `${value ?? 0} %`,
    },
  ];

  const getTableColumns = () => {
    const type = searchForm.getFieldValue("type");

    if (type === "assemble") return columnsAssemble;
    if (type === "checklist") {
      return [
        ...columns.filter(
          (column: any) => !["filledAt", "sentToApp"].includes(column.key ?? "")
        ),
        ...checklistReportColumns,
      ];
    }

    return columns.filter((column: any) => column.key !== "seller");
  };

  const getExportColumns = () => {
    const type = searchForm.getFieldValue("type");

    if (type === "assemble") return columnsAssemble;
    if (type === "checklist") return [...columns, ...checklistReportColumns];

    return columns;
  };

  const handleExport = () => {
    setExportLoading(true);
    const excel = new Excel();
    excel
      .addSheet("Tickets")
      .addColumns([
        ...getExportColumns(),
        {
          title: "Descrição",
          dataIndex: "description",
          key: "description",
        },
        ...(["measuring", "checklist"].includes(searchForm.getFieldValue("type"))
          ? [
              {
                title: "Telefone 1",
                dataIndex: "phone1",
                key: "phone1",
              },
              {
                title: "Telefone 2",
                dataIndex: "phone2",
                key: "phone2",
              },
            ]
          : []),
      ])
      .addDataSource(
        data.map((item) => ({
          ...item,
          description: item.description?.replace(/(<([^>]+)>)/gi, ""),
        })),
        {
          str2Percent: true,
        }
      )
      .saveAs("Tickets.xlsx");
    toast.success("Exportação concluída. Download iniciado");
    setExportLoading(false);
  };

  const handleRow = (record: any) => {
    return {
      onClick: (event: any) => {
        window.open(
          `/${
            searchForm.getFieldValue("type") === "measuring"
              ? "medicao"
              : searchForm.getFieldValue("type") === "assemble"
                ? "montagem"
                : "checklist"
          }?id=${record.key}`
        );
      },
    };
  };
  return (
    <div className="relative">
      {data.length > 0 && (
        <Button
          type="dashed"
          icon={<DownloadOutlined />}
          size="small"
          className="absolute z-20 -top-8 right-2"
          onClick={() => handleExport()}
          loading={exportLoading}
          disabled={exportLoading}
        >
          Exportar
        </Button>
      )}
      <Table
        loading={loading}
        onRow={handleRow}
        rowClassName={"cursor-pointer"}
        dataSource={loading ? [] : data}
        columns={
          getTableColumns()
        }
        className="z-10"
      />
    </div>
  );
};
